/**
 * The attract screen. Anything ends it.
 *
 * Full-bleed brand yellow, and the only screen in the application that is. It
 * is what a customer sees from across the room, and what tells them the panel
 * is a kiosk rather than a monitor somebody left on — so the mark is large, the
 * instruction is larger, and there is nothing else to read.
 *
 * **It is composed for 1080x1920 rather than centred in it.** Everything was
 * in one `justify-center` stack, which on a panel this tall put the mark at the
 * middle of the glass with a third of the screen empty above and below — and
 * the language switch, the one thing on here that is actually pressed, ended
 * up above the height a hand reaches. The mark now sits in the upper band
 * where it is read from across the shop, the instruction under it, and the
 * language sits low where it can be reached.
 *
 * What is *not* here: the old app's "sync SKUs" button, which existed because
 * automatic catalogue sync had been disabled and left a `startHourlySkuSync`
 * import unused with a comment saying so. The catalogue arrives over the socket.
 *
 * What *is* here, restored from the old app: the language switch. It sits on
 * the attract screen because that is the one moment a customer is not yet in
 * the middle of anything.
 */
import { useT } from '@/i18n'
import { useKiosk } from '@/store/kiosk'
import { LocaleSwitch } from '@/components/locale-switch'
import { Wordmark } from '@/components/brand'
import { Notice } from '@/components/ui'

export function Welcome() {
  const t = useT()
  const setStep = useKiosk((s) => s.setStep)
  const branchClosed = useKiosk((s) => s.branchClosed)
  const online = useKiosk((s) => s.online)

  return (
    /*
      A div and not a <button type="button">: the language switch inside it is itself a group
      of buttons, and a button inside a button is invalid HTML that browsers
      resolve by dropping one of them. The whole surface is still the target —
      `onClick` on the container, with a keyboard path for the browser till.
    */
    // biome-ignore lint/a11y/useSemanticElements: <button> is what the comment above rules out — the language switch inside is itself a group of buttons
    <div
      role="button"
      tabIndex={branchClosed ? -1 : 0}
      aria-disabled={branchClosed}
      onClick={() => !branchClosed && setStep('order-type')}
      onKeyDown={(event) => {
        if (branchClosed) return
        if (event.key === 'Enter' || event.key === ' ') setStep('order-type')
      }}
      className="relative flex h-full w-full flex-col items-center overflow-hidden bg-[var(--color-brand)] p-10 text-center"
    >
      {/*
        A wash, not a pattern. The yellow is flat everywhere else in the
        application and a single soft light behind the mark is what stops a
        1080×1920 panel of one colour reading as a screen that has failed to
        paint. It is centred on the mark rather than on the screen, because the
        mark is no longer in the middle of the screen.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(55% 32% at 50% 30%, oklch(1 0 0 / 0.5), transparent 70%)' }}
      />

      <div className="animate-fade-in relative flex flex-1 flex-col items-center justify-center gap-16 pb-10">
        {/*
          Straight on the yellow. The official kit has an all-ink lockup
          for this ground, so the cup does not need a paper disc to stay
          visible.
        */}
        <Wordmark on="brand" className="w-[min(36rem,85%)]" />

        {branchClosed ? (
          <Notice tone="bad">{t('welcome.closed')}</Notice>
        ) : (
          /*
            One line, and no tagline under it. The mark above already says
            whose kiosk this is; a second wordmark under the instruction was
            the same fact twice on the only screen that has room to say
            nothing.
          */
          <p className="display animate-nudge text-[length:var(--text-kiosk-2xl)] text-[var(--color-brand-ink)] uppercase">
            {t('welcome.tap')}
          </p>
        )}
      </div>

      {/*
        The bottom band. On a floor-standing panel this is the part a hand
        reaches without being asked to raise it, so it is where the one control
        on this screen lives — and where the offline strip goes, so a customer
        reads it at the same height they read everything else.
      */}
      {/*
        The language switch stays up when the branch is closed. It used to go
        with the rest of the band, which meant the one screen a customer cannot
        act on was also the one they could not translate — so somebody who reads
        only English met an unexplained dark panel. The offline strip still goes,
        because a closed shop is not an outage and saying both is noise.
      */}
      <div className="relative flex shrink-0 flex-col items-center gap-8 pb-6">
        <LocaleSwitch />
        {!branchClosed && !online && <Notice tone="warn">{t('welcome.offline')}</Notice>}
      </div>
    </div>
  )
}
